import React from 'react';
import {View} from 'react-native';
import {Formik, FormikHelpers} from 'formik';
import {observer} from 'mobx-react-lite';
import * as Yup from 'yup';

import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
// import CheckboxComponent from '../../components/ui-controls/web/CheckBox';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {Analytics} from '../../utils/analytics/analytics';
import {
  EventName,
  EventScreenName,
  EventSourceType,
} from '../../utils/analytics/events';
import {cleanPhoneNumber} from '../../utils/phoneNumber';
import {rewardsMarketingName} from '../../utils/rewards';

import {SignInSchema} from './validationSchema';

const RewardsSignInScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_SIGN_IN>) => {
    const {rewardsStore} = useStores();

    const onSubmit = async (
      values: SignInValues,
      {setFieldError, setErrors, setSubmitting}: FormikHelpers<SignInValues>,
    ) => {
      let phoneNumber = cleanPhoneNumber(values.phoneNumber);

      setSubmitting(true);
      try {
        const result = await apiService.rewardsCheckAccountExists({
          phoneNumber,
        });
        if (result?.status === 200) {
          rewardsStore.setPhoneNumber(phoneNumber);
          if (result.data?.exists) {
            navigation.navigate(AppRoute.REWARDS_WELCOME_BACK);
          } else {
            navigation.navigate(AppRoute.REWARDS_SIGN_UP);
          }
        } else {
          setFieldError('phoneNumber', 'Something went wrong');
        }
      } catch (e) {}
      setSubmitting(false);
    };

    const validationSchema = SignInSchema;

    type SignInValues = Yup.InferType<typeof validationSchema>;

    const defaultValues: SignInValues = {
      phoneNumber: '',
    };

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <Formik
          initialValues={defaultValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}>
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            initialValues,
            setFieldValue,
            setFieldError,
            setFieldTouched,
            touched,
            values,
            isSubmitting,
          }) => {
            return (
              <ContentView
                parentStyle={{flex: 1, paddingBottom: 16}}
                style={{flex: 1}}>
                <RewardsProgramImage variant="onLight" />

                <View>
                  <Text variant="heading1">WELCOME!</Text>
                </View>
                <Text>
                  This is the first step to unlocking FREE member benefits by
                  creating your {rewardsMarketingName} account.
                  {'\n\n'}
                  Let's start with your mobile number.
                </Text>

                <TextInput
                  containerStyle={{marginTop: 36}}
                  onChangeText={text => {
                    // ios safari autofill doesn't include country code
                    // when autofill number starting with +61 so add it here
                    if (text.length === 9 && text?.[0] === '4') {
                      text = '+61' + text;
                    }
                    handleChange('phoneNumber')(text);
                  }}
                  value={values.phoneNumber}
                  accessibilityLabel="Enter your mobile number"
                  autoCapitalize="none"
                  autoComplete="tel"
                  errorMessageWhenTouched={errors.phoneNumber}
                  keyboardType="phone-pad"
                  label="Enter your mobile number"
                  maxLength={20}
                  onBlur={handleBlur('phoneNumber')}
                  touched={touched.phoneNumber}
                />
                <View style={{alignItems: 'flex-end', marginTop: 16}}>
                  <Button
                    width={194}
                    title={'CONTINUE'}
                    onPress={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  />
                </View>
              </ContentView>
            );
          }}
        </Formik>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsSignInScreen;
